/**
 * PreVisitSectionCard — CARD level (Claude Design round 7). The chart's section card for the Today
 * preview: white card on the card border and shadow-2, a caps label in the section's wayfinding ink
 * (SYSTEM-RULES §2, the INK, never the fill) with its count, then the shipped ClinicalTermRow rows.
 * Read-only: the preview never edits.
 */
import type { ReactNode } from 'react';
import { cn } from '@scripsteam/scrips-signal-ds';
import { V2_CARD_SURFACE, V2_EYEBROW, V2_META_STRONG, V2_BODY } from '@/shared/ui/design-v2';

export type Wayfind = 'problems' | 'medications' | 'history' | 'neutral' | 'allergies';

/** Literal strings so Tailwind sees every class. */
const WAYFIND_INK: Record<Wayfind, string> = {
  problems: 'text-wayfind-problems-ink',
  medications: 'text-wayfind-medications-ink',
  history: 'text-wayfind-history-ink',
  neutral: 'text-wayfind-neutral-ink',
  allergies: 'text-wayfind-allergies-ink',
};

export function PreVisitSectionCard({
  fhir,
  title,
  wayfind,
  count,
  emptyText,
  loading,
  children,
}: {
  fhir: string;
  title: string;
  wayfind: Wayfind;
  count?: number;
  emptyText: string;
  loading?: boolean;
  children?: ReactNode[];
}) {
  const has = (children?.length ?? 0) > 0;
  return (
    <section
      data-block={fhir}
      aria-label={title}
      className={cn('min-w-0 rounded-card px-4 py-3 flex flex-col gap-2', V2_CARD_SURFACE)}
    >
      <h3 className="m-0 flex items-baseline gap-2">
        <span className={cn(V2_EYEBROW, WAYFIND_INK[wayfind])}>{title}</span>
        {count != null && !loading && <span className={V2_META_STRONG}>{count}</span>}
      </h3>
      {has ? (
        <ul className="m-0 p-0 list-none flex flex-col">{children}</ul>
      ) : (
        <p className={cn('m-0 text-text-secondary', V2_BODY)}>{loading ? 'Loading…' : emptyText}</p>
      )}
    </section>
  );
}
